import { driver, type DriveStep } from "driver.js";
import "driver.js/dist/driver.css";
import "./tour.css";
import type { TourStep } from "./types";
import { buildTourLegs, type TourLeg } from "./legs";
import { resolveDetailTargetId } from "./detailTarget";

// Scoping class on the popover so tour.css can out-specify driver.css
// without !important.
const POPOVER_CLASS = "ess-tour-popover";

const ANCHOR_POLL_MS = 150;
const ANCHOR_TIMEOUT_MS = 5000;

export interface StartFullTourOptions {
  /**
   * react-router's navigate. Passed in rather than pulled from a hook so
   * this module stays hook-free — and it MUST be SPA navigation: a
   * `window.location` assignment reloads the page and kills the tour
   * mid-flight.
   */
  navigate: (path: string) => void;
  /** Where the user was when they started, to return them there. */
  currentPath: string;
  /** False for an employee-only user — see `buildTourLegs`. */
  includeOverview?: boolean;
}

// An anchor can legitimately appear more than once with only one copy
// rendered — the New Suggestion CTA is a desktop Button plus a mobile
// IconButton, each hidden at the other's breakpoint. Take the first
// laid-out match rather than the first in document order.
function findVisibleAnchor(anchor: string): Element | null {
  const nodes = document.querySelectorAll(`[data-tour="${anchor}"]`);
  for (let i = 0; i < nodes.length; i += 1) {
    if (nodes[i].getClientRects().length > 0) return nodes[i];
  }
  return null;
}

/**
 * Polls until any of `anchors` is on screen. Resolves false on timeout,
 * which the caller reads as "this page never rendered — skip its leg".
 */
export function waitForAnyAnchor(
  anchors: string[],
  timeoutMs = ANCHOR_TIMEOUT_MS,
  intervalMs = ANCHOR_POLL_MS,
): Promise<boolean> {
  const present = () => anchors.some((a) => findVisibleAnchor(a) !== null);
  if (present()) return Promise.resolve(true);

  return new Promise((resolve) => {
    const startedAt = Date.now();
    const timer = window.setInterval(() => {
      if (present()) {
        window.clearInterval(timer);
        resolve(true);
      } else if (Date.now() - startedAt >= timeoutMs) {
        window.clearInterval(timer);
        resolve(false);
      }
    }, intervalMs);
  });
}

function toDriveSteps(steps: TourStep[]): DriveStep[] {
  const live: DriveStep[] = [];
  steps.forEach((step) => {
    const popover = { title: step.title, description: step.description };
    if (!step.anchor) {
      live.push({ popover });
      return;
    }
    // Role- and state-gated UI (committee evaluation cards, the action
    // bar, the collapsed mobile drawer) drops out here without the tour
    // needing to know anything about the role model.
    const element = findVisibleAnchor(step.anchor);
    if (element) live.push({ element, popover });
  });
  return live;
}

function samePath(a: string, b: string): boolean {
  const strip = (p: string) => p.replace(/\/+$/, "") || "/";
  return strip(a) === strip(b);
}

/**
 * Navigates to the leg's route, waits for its page to render, and filters
 * its steps down to what is actually on screen. An empty result means the
 * caller should skip this leg.
 */
async function prepareLeg(
  leg: TourLeg,
  navigate: (path: string) => void,
): Promise<DriveStep[]> {
  if (leg.route && !samePath(window.location.pathname, leg.route)) {
    navigate(leg.route);
  }
  const anchors = leg.steps
    .map((s) => s.anchor)
    .filter((a): a is string => Boolean(a));
  if (anchors.length > 0 && !(await waitForAnyAnchor(anchors))) return [];
  return toDriveSteps(leg.steps);
}

/**
 * Drives one leg. The two callbacks are mutually exclusive by
 * construction — see the comments on each hook.
 */
function driveLeg(
  steps: DriveStep[],
  onDone: () => void,
  onQuit: () => void,
): void {
  const instance = driver({
    showProgress: true,
    stagePadding: 6,
    popoverClass: POPOVER_CLASS,
    steps,
    // Fires ONLY on the last step, for the Done button, ArrowRight, and
    // advance-on-click alike — driver.js resolves all three through the
    // same handler lookup, which prefers onDoneClick once there is no
    // next step. Supplying it replaces the built-in teardown, so the
    // destroy() call is ours to make.
    onDoneClick: () => {
      instance.destroy();
      onDone();
    },
    // Esc, the X button and overlay clicks land here instead, and never
    // reach onDoneClick. Supplying it likewise suppresses the built-in
    // teardown. The public destroy() deliberately skips this hook, so
    // calling it here cannot recurse.
    onDestroyStarted: () => {
      instance.destroy();
      onQuit();
    },
  });
  instance.drive();
}

/**
 * Runs the full multi-page tour: the register, the overview, the
 * submission form, a real suggestion in detail, then a sign-off. Each leg
 * navigates itself and is skipped if its page never renders.
 *
 * Resolves true once the first leg is driving, false if it could not
 * start at all (e.g. an unverified user who cannot reach /suggestions) so
 * the caller can say so.
 */
export async function startFullTour({
  navigate,
  currentPath,
  includeOverview = true,
}: StartFullTourOptions): Promise<boolean> {
  const legs = buildTourLegs(await resolveDetailTargetId(), {
    includeOverview,
  });

  const goHome = () => {
    if (!samePath(window.location.pathname, currentPath)) navigate(currentPath);
  };

  // Walks forward from `index` to the first leg that has something to
  // show. Legs after the first are skipped individually; only the first
  // failing outright aborts the tour (see below).
  const advanceFrom = async (index: number): Promise<void> => {
    for (let i = index; i < legs.length; i += 1) {
      const steps = await prepareLeg(legs[i], navigate);
      if (steps.length === 0) continue;
      driveLeg(
        steps,
        () => {
          void advanceFrom(i + 1);
        },
        goHome,
      );
      return;
    }
    goHome();
  };

  // The first leg is the orientation on /suggestions. If it cannot run,
  // the user cannot reach the app's main surfaces at all, so there is no
  // point walking the rest.
  const first = await prepareLeg(legs[0], navigate);
  if (first.length === 0) {
    goHome();
    return false;
  }
  driveLeg(
    first,
    () => {
      void advanceFrom(1);
    },
    goHome,
  );
  return true;
}
